import styles from "./Header.module.scss";
import { NextRouter, useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { clientRoutes } from "helpers/clientRoutes";
import Navigation from "../Navigation/Navigation";
import { useAuth } from "hooks/authHooks";
import Profile from "components/Profile/Profile";
import _isEmpty from "lodash/isEmpty";
import { TestsPincodeSearch } from "../Tests/TestsPincodeSearch";
import dynamic from "next/dynamic";
import { useParams } from "hooks/routerHooks";
import { useGlobalDiagnosticsCart } from "components/Tests/DiagnosticsCartProviderGlobal";
import { eventTracking } from "clevertapTracking";
import {
  getPatientAttributes,
  getPatientDetails,
  isConsultRoute,
  readableParam,
  validRedirectUrlPaths,
} from "helpers/commonHelpers";
import { useShoppingCart } from "components/MedicinesCartProvider";
import LoginPopup from "../Auth/LoginPopup";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMobileDetect from "../../helpers/useMobileDetect";
import { GenerateIcon } from "helpers/iconGenerator";
import { ICONS_PATHS, PATHTOSECTIONMAPPER } from "helpers/constants";
import { DEFAULT_CITY_NAME } from "strings/AppConfig";
import { useOTP } from "components/OTPProvider";
import { SignedinState } from "components/enums/SignedinState";
import Link from "next/link";
import { dataLayerTracking } from "gtmTracking";
const DynamicProtectedWithLoginPopup = dynamic(
  () =>
    import("components/ProtectedWithLoginPopup").then(
      (defaultComponent) => defaultComponent.ProtectedWithLoginPopup
    ),
  { ssr: false }
);

interface HeaderProps {
  backArrowVisible?: boolean;
  isWebView?: boolean;
  backLocation?: string;
  showCompanyLogo?: boolean;
  isRouterFromPayCare?: boolean;
  isSymptomChecker?: boolean;
  mWebHeaderHide?: boolean;
  sectionName?: string;
  headerSize?: string;
}

const Header: React.FC<HeaderProps> = (props) => {
  const { isSigningIn, isSignedIn, signOut } = useAuth();
  const { setVerifyOtpError } = useOTP();
  const [profileVisible, setProfileVisible] = useState<boolean>(false);
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [currentPath, setCurrentPath] = useState<string>("");
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const [userSubscriptions, setUserSubscriptions] = useState<any>([]);
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [showMedicineLocation, setShowMedicineLocation] = useState<boolean>(
    false
  );
  const { currentPatient } = useAuth();
  const patientDetails = getPatientDetails(currentPatient);
  const { setCartIconClicked, diagnosticAddress } = useGlobalDiagnosticsCart();
  const node = useRef(null);
  const router = useRouter() as MyRouter;
  const { cartItems } = useShoppingCart();
  const iconPathsBackWhite = ICONS_PATHS.slice(0, 1);
  const currentDevice = useMobileDetect();
  type CustomNextRouter = Omit<NextRouter, "query">;
  type MyRouter = CustomNextRouter & {
    query: {
      canonicalName: string | undefined;
      orderId: string | undefined;
      testlisting: string | undefined;
      searchString: string | undefined;
      categoryList: string | undefined;
      testlistingCategory: string | undefined;
      slugName: string[] | undefined;
    };
  };
  const {
    showCompanyLogo = true,
    isWebView = false,
    backArrowVisible = false,
    backLocation,
    isSymptomChecker = false,
    mWebHeaderHide = false,
    sectionName,
    headerSize,
  } = props;
  const diagnosticsRoutes = [
    clientRoutes.tests(),
    clientRoutes.testDetailsWithCity(
      router?.query?.canonicalName
        ? router?.query?.canonicalName[0]
        : router?.query?.canonicalName,
      diagnosticAddress?.cityName
        ? readableParam(diagnosticAddress?.cityName.toLowerCase())
        : DEFAULT_CITY_NAME.toLowerCase()
    ),
    clientRoutes.testDetails(
      router?.query?.canonicalName
        ? router?.query?.canonicalName[0]
        : router?.query?.canonicalName
    ),
    clientRoutes.searchByTest(router?.query?.searchString),
    clientRoutes.testsCircleLanding(),
    clientRoutes.testListing(encodeURI(router?.query?.testlisting)),
    clientRoutes.testListing(encodeURIComponent(router?.query?.testlisting)),
    clientRoutes.testsCart(),
    clientRoutes.categoryListing(encodeURI(router?.query?.categoryList)),
    clientRoutes.testListingCategory(
      encodeURI(router?.query?.testlistingCategory)
    ),
    clientRoutes.previousOrders(),
    clientRoutes.diagnosticListing(router?.query?.slugName?.[0]),
  ];

  const params = useParams<{
    searchMedicineType: string;
    searchText: string;
    sku: string;
    orderId: string;
    itemId: string;
    searchType: string;
    searchTestText: string;
    widgetTitle: string;
  }>();
  const avatarRef = useRef(null);

  const cartIconClickedHandler = () => {
    const currentPath = router.asPath;
    const diagnosticsCartItems = JSON.parse(
      localStorage?.getItem(`diagnosticsCartItems`)
    );
    const isCartEmpty = !diagnosticsCartItems?.length;

    dataLayerTracking({
      event: "icon_click",
      icon_name: "Cart",
      section_name: sectionName,
    });

    // If cart empty, route to cart page, else current logic follows
    if (isCartEmpty) {
      router.push(clientRoutes.testsCart());
    } else {
      const isValidRedirectURL = validRedirectUrlPaths.some((path) =>
        new RegExp(path).test(currentPath)
      );
      if (currentPath === clientRoutes.welcome() || isValidRedirectURL) {
        isSignedIn === SignedinState.SIGNEDIN &&
          router.push(clientRoutes.testsWithCartOpen());
      } else {
        setCartIconClicked(true);
      }
    }
    if (isSignedIn === SignedinState.SIGNEDIN) {
      eventTracking("My Cart Clicked", {
        ...getPatientAttributes(patientDetails),
        Nav_src: "Top bar",
      });
    }
  };

  const handleClickProfileIcon = () => {
    dataLayerTracking({
      event: "icon_click",
      icon_name: "My Profile",
      section_name: sectionName,
    });
  };
  const pharmacyHomeViewedEvent = () => {
    eventTracking(
      "Pharmacy Home page viewed",
      isSignedIn === SignedinState.SIGNEDIN
        ? {
            ...getPatientAttributes(patientDetails),
            "Nav src": "Header",
            "Page Name": router.asPath,
          }
        : { "Nav src": "Header", "Page Name": router.asPath }
    );
  };
  const handleClick = (e: any) => {
    if (
      node.current &&
      node.current.contains(e.target) &&
      !_isEmpty(e.target)
    ) {
      // inside click
      return;
    }
    // outside click
    setProfileVisible(false);
  };

  useEffect(() => {
    const [currentPath] = router?.asPath?.split("?");
    var replaced = currentPath.replace(/%20/g, " ");
    const isMobileView = screen.width <= 768;
    setCurrentPath(replaced);
    setIsMobileView(isMobileView);
    setShowMedicineLocation(
      currentPath.includes("/medicine") ||
        currentPath.includes("/medicines") ||
        currentPath.includes("/specialties")
    );

    // add when mounted
    document.addEventListener("mousedown", handleClick);
    // return function to be called when unmounted
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [router?.pathname, router?.asPath]);

  const MedicineRoutes = [
    clientRoutes.medicines(),
    clientRoutes.searchByMedicine(
      params && params.searchMedicineType,
      params && params.searchText
    ),
    clientRoutes.medicineCategoryDetails(
      params && params.searchMedicineType,
      params && params.searchText,
      params && params.sku
    ),
    clientRoutes.medicineDetails(params && params.sku),
    clientRoutes.medicineAllBrands(),
    clientRoutes.prescriptionsLanding(),
    clientRoutes.prescriptionReview(),
    clientRoutes.medicinePrescription(),
    clientRoutes.medicineSearch(),
  ];

  const labTestsRoutes = !!(
    diagnosticsRoutes.find((route) => route === currentPath) ||
    clientRoutes.orders() === currentPath ||
    clientRoutes.orderdetails(router?.query?.orderId) === currentPath ||
    clientRoutes.uploadPrescription() === currentPath
  );

  // show location picker for diag routes, once google maps script is loaded
  const showLocationPicker =
    diagnosticsRoutes.find((route) => route === currentPath) &&
    !!window?.google?.maps &&
    typeof window.google.maps === "object";

  return (
    <header
      className={`${styles.header} ${
        isSymptomChecker ? styles.hideHeader : ""
      } ${mWebHeaderHide ? styles.mHide : ""}`}
      id="fixedHeaderCT"
    >
      <div
        className={`${styles.container} ${
          headerSize ? styles[headerSize] : ""
        }`}
      >
        <div className={styles.headerContent}>
          {!isWebView && (
            <div className={styles.logoWrapper}>
              {showCompanyLogo && (
                <Link
                  href={
                    isConsultRoute(currentPath)
                      ? clientRoutes.specialityListing()
                      : labTestsRoutes
                      ? clientRoutes.tests()
                      : clientRoutes.welcome()
                  }
                >
                  <a
                    onClick={(e) => {
                      eventTracking("Home Page Icon Clicked", {
                        ...getPatientAttributes(patientDetails),
                        Nav_src: "Top bar",
                      });
                      dataLayerTracking({
                        event: "icon_click",
                        icon_name: "Apollo247 Logo",
                        section_name: sectionName,
                      });
                    }}
                    title="Home"
                    className={backArrowVisible ? styles.logo : styles.logoOnly}
                  >
                    <img
                      src={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/ic_logo.png`}
                      title={"Online Doctor Consultation & Medicines"}
                      alt={"Online Doctor Consultation & Medicines"}
                      width="100%"
                      height="100%"
                    />
                  </a>
                </Link>
              )}
            </div>
          )}
          <div id="webengageNotificationContainer"></div>

          <div
            className={`${styles.locationPicker} ${
              !showLocationPicker ? styles.displayNone : ""
            }`}
          >
            <TestsPincodeSearch />
          </div>

          <div className={styles.menuContainer}>
            <Navigation />
            {!isConsultRoute(currentPath) && (
              <div className={styles.cartContainer}>
                <span
                  className="icon-ic_cart"
                  onClick={cartIconClickedHandler}
                />

                {cartItems.length > 0 && (
                  <span className={styles.badge}>{cartItems.length}</span>
                )}
              </div>
            )}

            <div className={styles.userOptions}>
              {/* Also add logic for login popup visible */}
              {isSignedIn === SignedinState.SIGNEDOUT ? (
                <>
                  <DynamicProtectedWithLoginPopup
                    handleClickProfileIcon={handleClickProfileIcon}
                  >
                    {({ protectWithLoginPopup }) => {
                      if (
                        props.isRouterFromPayCare &&
                        isSignedIn !== SignedinState.SIGNEDIN
                      )
                        protectWithLoginPopup();
                      return (
                        <>
                          <div
                            id="loginPopup"
                            className={`${styles.userCircle} `}
                            onClick={() =>
                              isSignedIn === SignedinState.SIGNEDIN
                                ? clientRoutes.medicinesCart()
                                : protectWithLoginPopup()
                            }
                            ref={avatarRef}
                            title={"Login/SignUp"}
                          >
                            {isSigningIn ? (
                              <CircularProgress />
                            ) : (
                              <span className="icon-ic_account" />
                            )}
                          </div>
                        </>
                      );
                    }}
                  </DynamicProtectedWithLoginPopup>
                  <LoginPopup anchorRef={avatarRef.current} />
                </>
              ) : isSignedIn === SignedinState.SIGNEDIN ? (
                <Profile sectionName={sectionName} />
              ) : null}
              {backArrowVisible && (
                <>
                  <div
                    className={styles.backArrow}
                    onClick={() => {
                      dataLayerTracking({
                        event: "button_click",
                        button_name: "Back Arrow",
                        section_name: sectionName,
                      });
                      const clevertapData = {
                        "Order id": router.query?.orderId?.toString(),
                        UHID: currentPatient?.uhid?.toString(),
                      };
                      if (currentPath.includes("payments/diagnostic")) {
                        eventTracking(
                          "Diagnostic payment aborted",
                          clevertapData
                        );
                      }
                      currentDevice.isAndroid() && isWebView
                        ? (window.location.href =
                            backLocation || clientRoutes.welcome())
                        : router.back();
                    }}
                  >
                    <GenerateIcon
                      pathName={iconPathsBackWhite}
                      iconClass={"icon-ic_back_white"}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
